import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import { Save, X } from 'lucide-react';
import { toast } from 'react-toastify';
import '../../../../../styles/components/action-modal.scss';
import i18n from '../../../../../i18n';
import { createPaymentAccount, updatePaymentAccount } from '../services/bankAccountServices';

const EMPTY_FORM = {
  bank_name: '',
  branch_name: '',
  account_name: '',
  account_number: '',
  iban: '',
  swift_code: '',
  currency: 'OMR',
  is_default: false,
  remarks: '',
};

export class PaymentBankAccountModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      form: { ...EMPTY_FORM },
      errors: {},
      submitting: false,
    };
  }

  componentDidUpdate(prevProps) {
    // Hydrate form whenever the modal is opened, either for create or edit.
    if (this.props.show && !prevProps.show) {
      const { account } = this.props;

      this.setState({
        form: account
          ? {
              bank_name: account.bank_name || '',
              branch_name: account.branch_name || '',
              account_name: account.account_name || '',
              account_number: account.account_number || '',
              iban: account.iban || '',
              swift_code: account.swift_code || '',
              currency: account.currency || 'OMR',
              is_default: !!account.is_default,
              remarks: account.remarks || '',
            }
          : { ...EMPTY_FORM },
        errors: {},
        submitting: false,
      });
    }
  }

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        [name]: type === 'checkbox' ? checked : value,
      },
      errors: {
        ...prevState.errors,
        [name]: undefined,
      },
    }));
  };

  validate = () => {
    const { form } = this.state;
    const errors = {};

    if (!form.bank_name.trim()) errors.bank_name = i18n.t('field_required');
    if (!form.account_name.trim()) errors.account_name = i18n.t('field_required');
    if (!form.account_number.trim()) errors.account_number = i18n.t('field_required');
    if (!form.iban.trim()) errors.iban = i18n.t('field_required');

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = async () => {
    if (!this.validate()) return;

    const { account, onSaved } = this.props;
    const { form } = this.state;
    const isEdit = !!account;

    const payload = {
      ...form,
      iban: form.iban.trim().toUpperCase().replace(/\s+/g, ''),
      account_number: form.account_number.trim(),
    };

    this.setState({ submitting: true });

    try {
      const response = isEdit
        ? await updatePaymentAccount(account.id, payload)
        : await createPaymentAccount(payload);

      if (response && response.success) {
        toast.success(
          response.message ||
            (isEdit ? i18n.t('updated_successfully') : i18n.t('created_successfully'))
        );
        onSaved?.(response.data);
        this.handleClose();
      } else {
        // Surface field-level validation errors coming back from the API, if any.
        const apiErrors = response?.data?.[0] || {};
        this.setState({ errors: apiErrors });
        toast.error(response?.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleClose = () => {
    this.setState({ form: { ...EMPTY_FORM }, errors: {}, submitting: false });
    this.props.onClose();
  };

  render() {
    const { show, account } = this.props;
    const { form, errors, submitting } = this.state;
    const isEdit = !!account;

    return (
      <CustomModal
        isOpen={show}
        header={isEdit ? i18n.t('edit_bank_account') : i18n.t('add_bank_account')}
        onClose={this.handleClose}
      >
        <div className="action-modal-container">
          <div className="payment-details-card">
            <div className="details-grid">
              <div className="detail-item-new full-width">
                <label className="reject-label">
                  {i18n.t('bank_name')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="bank_name"
                  className={`reject-textarea ${errors.bank_name ? 'error' : ''}`}
                  value={form.bank_name}
                  onChange={this.handleChange}
                  disabled={submitting}
                  maxLength={150}
                />
                {errors.bank_name && <span className="error-message">{errors.bank_name}</span>}
              </div>

              <div className="detail-item-new full-width">
                <label className="reject-label">{i18n.t('branch_name')}</label>
                <input
                  type="text"
                  name="branch_name"
                  className="reject-textarea"
                  value={form.branch_name}
                  onChange={this.handleChange}
                  disabled={submitting}
                  maxLength={150}
                />
              </div>

              <div className="detail-item-new full-width">
                <label className="reject-label">
                  {i18n.t('account_name')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="account_name"
                  className={`reject-textarea ${errors.account_name ? 'error' : ''}`}
                  value={form.account_name}
                  onChange={this.handleChange}
                  disabled={submitting}
                  maxLength={150}
                />
                {errors.account_name && (
                  <span className="error-message">{errors.account_name}</span>
                )}
              </div>

              <div className="detail-item-new">
                <label className="reject-label">
                  {i18n.t('account_number')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="account_number"
                  className={`reject-textarea ${errors.account_number ? 'error' : ''}`}
                  value={form.account_number}
                  onChange={this.handleChange}
                  disabled={submitting}
                  maxLength={100}
                />
                {errors.account_number && (
                  <span className="error-message">{errors.account_number}</span>
                )}
              </div>

              <div className="detail-item-new">
                <label className="reject-label">
                  {i18n.t('iban')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="iban"
                  className={`reject-textarea ${errors.iban ? 'error' : ''}`}
                  value={form.iban}
                  onChange={this.handleChange}
                  disabled={submitting}
                  maxLength={100}
                />
                {errors.iban && <span className="error-message">{errors.iban}</span>}
              </div>

              <div className="detail-item-new">
                <label className="reject-label">{i18n.t('swift_code')}</label>
                <input
                  type="text"
                  name="swift_code"
                  className="reject-textarea"
                  value={form.swift_code}
                  onChange={this.handleChange}
                  disabled={submitting}
                  maxLength={50}
                />
              </div>

              <div className="detail-item-new">
                <label className="reject-label">{i18n.t('currency')}</label>
                <input
                  type="text"
                  name="currency"
                  className="reject-textarea"
                  value={form.currency}
                  onChange={this.handleChange}
                  disabled={submitting}
                  maxLength={10}
                />
              </div>

              <div className="detail-item-new full-width">
                <label className="reject-label">{i18n.t('remarks')}</label>
                <textarea
                  name="remarks"
                  className="reject-textarea"
                  value={form.remarks}
                  onChange={this.handleChange}
                  disabled={submitting}
                  rows={3}
                />
              </div>

              <div className="detail-item-new full-width">
                <label className="reject-label checkbox-label">
                  <input
                    type="checkbox"
                    name="is_default"
                    checked={form.is_default}
                    onChange={this.handleChange}
                    disabled={submitting}
                  />
                  {i18n.t('set_as_primary_account')}
                </label>
              </div>
            </div>
          </div>

          <div className="action-buttons-container">
            <button
              className="modal-btn btn-cancel"
              onClick={this.handleClose}
              disabled={submitting}
            >
              <X size={18} />
              {i18n.t('cancel')}
            </button>

            <button
              className="modal-btn btn-success"
              onClick={this.handleSubmit}
              disabled={submitting}
            >
              <Save size={18} />
              {submitting ? i18n.t('saving') : i18n.t('save')}
            </button>
          </div>
        </div>
      </CustomModal>
    );
  }
}

export default PaymentBankAccountModal;
